import React, { useMemo, useState } from "react";

import { Divider, Box } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import BadgeJobStatus from "Components/Generic/BadgeJobStatus";
import { getDayOfYear, getYear, differenceInDays, startOfToday, startOfDay } from "date-fns";
import { Clock as IconClock, StarSlash as IconStar } from "iconsax-react";
import { DespatchTask } from "Models/despatch.task";
import { Event } from "react-big-calendar";
import { useTranslation } from "react-i18next";
import { useSpring } from "react-spring";
import useMeasure from "react-use-measure";
import { getOrderCustomerDisplayName, getCustomerFullname, getStaffFullname } from "Util/names";
import { useAppState } from "Context/StateContext";
import { getCompleteDateRangeLabel } from "Util/startDate";

import { PanelContent, panelStyles, PanelTitle, PanelCard, DateRange, PanelText } from "./styles";

interface PanelProps {
  title: string;
  order: number;
  date: Date;
  events?: Event[];
  onSelectEvent: (event: Event) => void;
}

interface TaskProps {
  event: Event;
  onSelectEvent: (event: Event) => void;
  currentDate: Date;
}

const TaskCard = ({ event, onSelectEvent, currentDate }: TaskProps) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const task = useMemo(() => {
    return event.resource.task as DespatchTask;
  }, [event.resource]);

  const team = useMemo(() => {
    if (task.team) return task.team;

    return task.teams?.find((t) => {
      const createdTo = new Date(t.createdTo!);
      return differenceInDays(startOfToday(), startOfDay(createdTo)) === 0;
    });
  }, [task, task.teams]);

  const dateRanged = useMemo(() => {
    return getCompleteDateRangeLabel({
      startDate: task.startDate,
      endDate: task.endDate,
      separator: "-",
      period: task.period,
      currentDate,
      timeSlots: task.timeSlots,
      preferences: currentUser?.preferences,
    });
  }, [task, currentDate, currentUser?.preferences]);

  const crewMembersValueLabel = useMemo(() => {
    const members = [...(team?.members ?? [])];

    if (team?.memberLeader) members.unshift(team?.memberLeader);

    return (
      members
        .map((member) => {
          const memberName = getStaffFullname(member);

          if (memberName) return memberName;

          return undefined;
        })
        .filter(Boolean) as string[]
    ).join(", ");
  }, [task, team]);

  const isVip = useMemo(() => {
    return !!task.job.vipTreatment;
  }, [event]);

  const isAssigned = useMemo(() => {
    return !!(team?.memberLeader && team?.members?.length);
  }, [task, team]);

  return (
    <PanelCard onClick={() => onSelectEvent(event)} isAssigned={isAssigned}>
      <Box display="flex" justifyContent="center" flexDirection="column" gap="8px">
        <Box display="flex" gap="8px" alignItems="center">
          {isVip && (
            <IconStar size={16} color={colors.rose800} fill={colors.white} variant="Bold" />
          )}
          <PanelTitle isVip={isVip} isAssigned={isAssigned}>
            {translate(task.type)}
          </PanelTitle>
        </Box>

        <DateRange>
          <IconClock size={16} />

          <span>{dateRanged}</span>
        </DateRange>
      </Box>

      <Divider flexItem orientation="vertical" />

      <Box display="flex" justifyContent="center" flexDirection="column" gap="8px">
        <PanelTitle>
          {event.resource.order
            ? getOrderCustomerDisplayName(event.resource.order)
            : translate("noInformationToShow")}
        </PanelTitle>

        <PanelText isPlaceholder={!task.job.destinations?.at(0)?.longAddress}>
          {task.job.destinations?.at(0)?.longAddress || translate("noInformationToShow")}
        </PanelText>
      </Box>

      <Divider flexItem orientation="vertical" />

      <Box display="flex" justifyContent="center" flexDirection="column" gap="8px" flex="1">
        <PanelTitle>{translate("team")}</PanelTitle>

        <PanelText isPlaceholder={!crewMembersValueLabel}>
          {crewMembersValueLabel || translate("noCrewAssigned")}
        </PanelText>
      </Box>

      <Box>
        <BadgeJobStatus value={task.status} style={{ height: "24px" }} />
      </Box>
    </PanelCard>
  );
};

const CollapsablePanel = ({ title, events, order, onSelectEvent, date }: PanelProps) => {
  const classes = panelStyles(order)();
  const { t: translate } = useTranslation();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [ref, bounds] = useMeasure();

  const panelContentAnimatedStyle = useSpring({
    height: isCollapsed ? 0 : bounds.height,
  });

  const togglePanel = () => {
    setIsCollapsed((isCollapsed) => !isCollapsed);
  };

  const renderEvents = () => {
    if (events && events.length === 0)
      return <span className={classes.noTasksText}>{translate("noTasksRegisteredForTheDay")}</span>;

    return events?.map((event: Event, index: number) => (
      <TaskCard key={index} event={event} onSelectEvent={onSelectEvent} currentDate={date} />
    ));
  };

  return (
    <div className={classes.panel}>
      <div data-cy="collapse-header" className={classes.panelHeading} onClick={togglePanel}>
        <span
          data-cy={`taskTab-${getDayOfYear(date)}-${getYear(date)}-title`}
          className={classes.title}
        >
          {title}
        </span>
      </div>

      <PanelContent style={panelContentAnimatedStyle}>
        <div className={classes.panelContent} ref={ref}>
          {renderEvents()}
        </div>
      </PanelContent>
    </div>
  );
};

export default CollapsablePanel;
